import { supabase } from '../config/supabase';
import { ExtensionUserStats } from '../types';

export interface User extends ExtensionUserStats {
  // Campos adicionais podem ser adicionados aqui
}

export interface UserFilters {
  search?: string;
  activityStatus?: 'active' | 'inactive' | 'abandoned';
  dateFrom?: string;
  dateTo?: string;
  minProfiles?: number;
  minLabels?: number;
}

export interface PaginationParams {
  page: number;
  limit: number;
}

export interface UsersResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Serviço para gerenciar usuários da extensão
 */
class UsersService {
  /**
   * Busca usuários com paginação e filtros
   */
  async getUsers(
    filters: UserFilters = {},
    pagination: PaginationParams = { page: 1, limit: 20 }
  ): Promise<UsersResponse> {
    try {
      let query = supabase.from('extension_user_stats').select('*', { count: 'exact' });

      // Aplicar filtros
      if (filters.search) {
        query = query.or(`email.ilike.%${filters.search}%`);
      }

      if (filters.activityStatus) {
        query = query.eq('activity_status', filters.activityStatus);
      }

      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }

      if (filters.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }

      if (filters.minProfiles !== undefined) {
        query = query.gte('profiles_saved', filters.minProfiles);
      }

      if (filters.minLabels !== undefined) {
        query = query.gte('labels_created', filters.minLabels);
      }

      // Aplicar paginação
      const offset = (pagination.page - 1) * pagination.limit;
      query = query.range(offset, offset + pagination.limit - 1);

      // Ordenar por última atividade (mais recente primeiro)
      query = query.order('last_activity', { ascending: false, nullsFirst: false });

      const { data, error, count } = await query;

      if (error) {
        console.error('Erro ao buscar usuários:', error);
        throw error;
      }

      const total = count || 0;
      const totalPages = Math.ceil(total / pagination.limit);

      return {
        users: (data || []) as User[],
        total,
        page: pagination.page,
        limit: pagination.limit,
        totalPages,
      };
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      throw error;
    }
  }

  /**
   * Busca detalhes de um usuário específico
   */
  async getUserById(userId: string): Promise<User | null> {
    try {
      const { data, error } = await supabase
        .from('extension_user_stats')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Usuário não encontrado
        }
        console.error('Erro ao buscar usuário:', error);
        throw error;
      }

      return data as User;
    } catch (error) {
      console.error('Erro ao buscar usuário:', error);
      return null;
    }
  }

  /**
   * Busca perfis salvos por um usuário
   */
  async getUserProfiles(userId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*, linkedin_profiles(*)')
        .eq('user_id', userId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar perfis do usuário:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar perfis do usuário:', error);
      return [];
    }
  }

  /**
   * Busca labels criadas por um usuário
   */
  async getUserLabels(userId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('user_profile_labels')
        .select('*')
        .eq('user_id', userId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar labels do usuário:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar labels do usuário:', error);
      return [];
    }
  }

  /**
   * Desativa um usuário (via Supabase Auth Admin API)
   * Nota: Requer permissões de admin
   */
  async deactivateUser(userId: string): Promise<boolean> {
    try {
      // Por enquanto, apenas registramos a ação
      // Para desativar realmente, precisaríamos usar a API Admin do Supabase
      console.log('Desativar usuário:', userId);
      return true;
    } catch (error) {
      console.error('Erro ao desativar usuário:', error);
      return false;
    }
  }

  /**
   * Ativa um usuário (via Supabase Auth Admin API)
   */
  async activateUser(userId: string): Promise<boolean> {
    try {
      // Por enquanto, apenas registramos a ação
      console.log('Ativar usuário:', userId);
      return true;
    } catch (error) {
      console.error('Erro ao ativar usuário:', error);
      return false;
    }
  }

  /**
   * Exporta lista de usuários
   */
  async exportUsers(filters: UserFilters = {}): Promise<User[]> {
    try {
      let query = supabase.from('extension_user_stats').select('*');

      // Aplicar mesmos filtros
      if (filters.search) {
        query = query.or(`email.ilike.%${filters.search}%`);
      }

      if (filters.activityStatus) {
        query = query.eq('activity_status', filters.activityStatus);
      }

      if (filters.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }

      if (filters.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }

      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao exportar usuários:', error);
        throw error;
      }

      return (data || []) as User[];
    } catch (error) {
      console.error('Erro ao exportar usuários:', error);
      throw error;
    }
  }
}

export default new UsersService();

